import { useCallback, useMemo, useState } from 'react';
import { Flex, Button } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { MessageCirclePlus, CheckCircle } from 'lucide-react';
import { useParams, Link as RouterLink, useSearchParams } from 'react-router-dom';

import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { TicketList, TicketsPageHeader, TicketDetailsArea } from '@duke-hq/svc-support-web';
import FilterBarLayout from '@duke-hq/filters/components/FilterBarLayout';
import FiltersProvider from '@duke-hq/filters/context/FiltersProvider';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { useAuthTenant } from '@duke-hq/app-provider-web/tenant/useAuthTenant';
import { TicketStatus, TicketResolution } from '@duke-hq/svc-api-db';
import { decodeDrn, Service } from '@duke-hq/drn';

import { sizeVars } from '@duke-hq/ui/constants/layout';
import { MessageTypeFilter } from './filters/MessageTypeFilter';
import { useChatActions } from './useChatActions';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';

function SupportTicketsContent() {
  const [searchQuery, setSearchQuery] = useState('');
  const { filters } = useFiltersContext();
  const { ticketId } = useParams<{ ticketId?: string }>();
  const session = useAuthTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'archived' ? 'archived' : 'open';

  const {
    data: ticketListData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = trpcTenant.support.listTickets.useInfiniteQuery(
    {
      status: activeTab === 'archived' ? TicketStatus.CLOSED : undefined,
      searchQuery: searchQuery || undefined,
      messageType: filters.messageType,
    },
    {
      getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
    },
  );

  const { data: selectedTicketData } = trpcTenant.support.getTicket.useQuery(
    { ticketId: ticketId ?? '' },
    { enabled: ticketId !== undefined },
  );

  const trpcUtils = trpcTenant.useUtils();

  const updateStatusMutation = trpcTenant.support.updateTicketStatus.useMutation({
    onSuccess: () => {
      void trpcUtils.support.getTicket.refetch({ ticketId: ticketId ?? '' });
      void trpcUtils.support.listTickets.refetch();
      void trpcUtils.support.getUnreadTicketCount.invalidate();
      void trpcUtils.notifications.unreadTicketCount.invalidate();
    },
  });

  const handleMarkResolved = () => {
    if (ticketId !== undefined) {
      updateStatusMutation.mutate({
        ticketId,
        status: TicketStatus.CLOSED,
        resolution: TicketResolution.SOLVED,
      });
    }
  };

  const chatActions = useChatActions(selectedTicketData?.chatUsers ?? {}, ticketId);

  const isAdminCreated =
    decodeDrn(selectedTicketData?.ticket.submitterDrn)?.service === Service.Admin;
  const showResolvedButton = selectedTicketData?.ticket.resolution === null && !isAdminCreated;

  const tickets = useMemo(
    () => ticketListData?.pages.flatMap(({ tickets }) => tickets) ?? [],
    [ticketListData?.pages],
  );

  const openCount = ticketListData?.pages[0]?.openCount ?? 0;

  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const updateTabInSearch = useCallback(
    (tab: string) => {
      const next = new URLSearchParams(searchParams);

      if (tab === 'archived') {
        next.set('tab', 'archived');
      } else {
        next.delete('tab');
      }

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const tabs = [
    {
      key: 'open',
      label: <Trans>Open</Trans>,
      count: openCount,
      isActive: activeTab === 'open',
      onClick: () => updateTabInSearch('open'),
    },
    {
      key: 'archived',
      label: <Trans>Archived</Trans>,
      isActive: activeTab === 'archived',
      onClick: () => updateTabInSearch('archived'),
    },
  ];

  return (
    <Flex direction="column" w="100%" h={`calc(100vh - ${sizeVars.navbar})`}>
      <TicketsPageHeader
        actions={
          <Flex flex={1} justify="flex-end">
            <Button
              as={RouterLink}
              to="/inbox/new"
              leftIcon={<MessageCirclePlus size={iconSize.md} />}
              variant="solid"
              size="sm"
            >
              <Trans>New Chat</Trans>
            </Button>
          </Flex>
        }
      >
        <SearchInput maxW={pxToRem(200)} onCommit={setSearchQuery} urlSync={false} />

        <FilterBarLayout>
          <MessageTypeFilter key="messageType" />
        </FilterBarLayout>
      </TicketsPageHeader>

      <Flex flex={1} overflow="hidden">
        <TicketList
          loading={isLoading}
          tabs={tabs}
          tickets={tickets}
          baseUrl="/inbox"
          selectedTicketId={ticketId}
          hasMore={hasNextPage ?? false}
          isFetchingMore={isFetchingNextPage}
          onEndReached={handleEndReached}
        />
        <TicketDetailsArea
          ticket={selectedTicketData?.ticket}
          currentUserDrn={session?.user?.drn}
          chatActions={chatActions}
          headerActions={
            showResolvedButton ? (
              <Button
                leftIcon={<CheckCircle size={14} />}
                variant="outline"
                colorScheme="green"
                size="xs"
                onClick={handleMarkResolved}
                isLoading={updateStatusMutation.isPending}
              >
                <Trans>Mark as resolved</Trans>
              </Button>
            ) : undefined
          }
        />
      </Flex>
    </Flex>
  );
}

export function SupportTicketsPage() {
  return (
    <FiltersProvider localStorageKey="provider-support-tickets-filters">
      <SupportTicketsContent />
    </FiltersProvider>
  );
}
